import { useEffect, useState } from "react";

interface DeviceInfo {
  isMobile: boolean;
  hasWalletExtension: boolean;
  isMobileWithoutExtension: boolean;
  userAgent: string;
  walletType: "metamask" | "okx" | "coinbase" | "generic" | "none";
  isOKXBrowser: boolean;
}

const getEthereumProviders = (): any[] => {
  const ethereum = (window as any).ethereum;

  if (!ethereum) {
    return [];
  }

  if (Array.isArray(ethereum.providers)) {
    return ethereum.providers;
  }

  return [ethereum];
};

export const useDeviceDetection = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    isMobile: false,
    hasWalletExtension: false,
    isMobileWithoutExtension: false,
    userAgent: "",
    walletType: "none",
    isOKXBrowser: false,
  });

  useEffect(() => {
    const checkDevice = () => {
      const userAgent = navigator.userAgent;
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          userAgent
        );

      // Check for OKX wallet browser specifically
      const isOKXBrowser =
        !!(window as any).okxwallet || userAgent.includes("OKApp");

      const ethereumProviders = getEthereumProviders();

      // Enhanced wallet detection, including EIP-6963-style provider arrays
      const hasOKXWallet =
        !!(window as any).okxwallet ||
        ethereumProviders.some(
          (provider) => provider.isOKXWallet || provider.isOkxWallet
        );
      const hasRabbyWallet = ethereumProviders.some(
        (provider) => provider.isRabby
      );
      const hasPhantomEthereum = ethereumProviders.some(
        (provider) => provider.isPhantom
      );
      const hasMetaMask = ethereumProviders.some(
        (provider) =>
          provider.isMetaMask &&
          !provider.isRabby &&
          !provider.isPhantom &&
          !provider.isOKXWallet &&
          !provider.isOkxWallet
      );
      const hasCoinbaseWallet =
        ethereumProviders.some((provider) => provider.isCoinbaseWallet) ||
        !!(window as any).coinbaseWalletExtension;
      const hasGenericEthereum = ethereumProviders.length > 0;

      const hasWalletExtension =
        hasOKXWallet || hasMetaMask || hasCoinbaseWallet || hasGenericEthereum;
      const isMobileWithoutExtension = isMobile && !hasWalletExtension;

      // Determine wallet type priority: OKX > MetaMask > Coinbase > Generic > None
      // Special handling: if multiple wallets detected, prefer non-Phantom for Ethereum
      let walletType: "metamask" | "okx" | "coinbase" | "generic" | "none" =
        "none";

      if (hasOKXWallet) {
        walletType = "okx";
      } else if (hasMetaMask) {
        walletType = "metamask";
      } else if (hasCoinbaseWallet) {
        walletType = "coinbase";
      } else if (hasRabbyWallet || hasPhantomEthereum) {
        walletType = "generic";
      } else if (hasGenericEthereum) {
        // If Phantom is present with other wallets, mark as generic to show conflict warning
        walletType = "generic";
      }

      setDeviceInfo({
        isMobile,
        hasWalletExtension,
        isMobileWithoutExtension,
        userAgent,
        walletType,
        isOKXBrowser,
      });
    };

    checkDevice();

    // Re-check when ethereum providers change
    const handleEthereumChange = () => {
      checkDevice();
    };

    // Listen for wallet changes
    if ((window as any).ethereum) {
      (window as any).ethereum.on("connect", handleEthereumChange);
      (window as any).ethereum.on("disconnect", handleEthereumChange);
    }

    return () => {
      if ((window as any).ethereum) {
        (window as any).ethereum.removeListener(
          "connect",
          handleEthereumChange
        );
        (window as any).ethereum.removeListener(
          "disconnect",
          handleEthereumChange
        );
      }
    };
  }, []);

  return deviceInfo;
};
